import React, { useEffect, useMemo, useState } from "react";
import { locale, tf } from "../i18n";
import { getMessagePreviewImage } from "../utils/messageImages";

// Дашборд: полоса из четырёх показателей, последние диалоги, активность за
// неделю, быстрые действия и таблица аккаунтов. Вид — классы .dsb-* и .ui-*
// в index.css: цвета там заданы переменными темы (тёмная, графит, светлая),
// поэтому здесь нет ни одного цвета.

// ── Время ──────────────────────────────────────────────────────────────

// Время последнего сообщения: lastMessageMs, а если его нет — дата и время
// из сводки («09.10.2026», «15:20», «Heute», «Gestern»).
const conversationMs = (item) => {
  const direct = Number(item?.lastMessageMs);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const date = String(item?.date || "").trim();
  const time = String(item?.time || "").trim();
  const timeMatch = time.match(/^(\d{1,2}):(\d{2})/);
  const base = new Date();
  if (/^(heute|сегодня|today)$/i.test(date)) {
    // сегодня
  } else if (/^(gestern|вчера|yesterday)$/i.test(date)) {
    base.setDate(base.getDate() - 1);
  } else {
    const dateMatch = date.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
    if (!dateMatch) return 0;
    const year = Number(dateMatch[3]) < 100 ? 2000 + Number(dateMatch[3]) : Number(dateMatch[3]);
    base.setFullYear(year, Number(dateMatch[2]) - 1, Number(dateMatch[1]));
  }
  base.setHours(timeMatch ? Number(timeMatch[1]) : 12, timeMatch ? Number(timeMatch[2]) : 0, 0, 0);
  return base.getTime();
};

const formatAgo = (ms, t) => {
  if (!ms) return "";
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - ms) / 60000);
  if (diffMin < 1) return t("сейчас");
  if (diffMin < 60) return tf`${diffMin} мин`;
  const date = new Date(ms);
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return t("вчера");
  return date.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit" });
};

// Русские числительные: 1 диалог, 2 диалога, 5 диалогов.
const pluralIndex = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 0;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1;
  return 2;
};

// ── Диалоги ────────────────────────────────────────────────────────────

// Архив «Сообщений» хранится в браузере по ключам диалога — тем же, что в
// MessagesTab (getConversationKeyCandidates): номер, ссылка или id вместе с
// аккаунтом. Архивные диалоги на дашборде не показываем.
const ARCHIVED_STORAGE_KEY_PREFIX = "kl_messages_archived_v1";
const readArchivedMap = (ownerId) => {
  try {
    const raw = window.localStorage.getItem(`${ARCHIVED_STORAGE_KEY_PREFIX}:${String(ownerId || "").trim() || "default"}`);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};
const conversationKeys = (item) => {
  const accountId = item?.accountId != null ? String(item.accountId).trim() : "";
  if (!accountId) return [];
  const keys = [];
  const conversationId = String(item.conversationId || "").trim();
  const conversationUrl = String(item.conversationUrl || "").trim();
  const id = String(item.id || "").trim();
  if (conversationId) keys.push(`${accountId}|cid:${conversationId}`, `${accountId}|${conversationId}`);
  if (conversationUrl) keys.push(`${accountId}|url:${conversationUrl}`, `${accountId}|${conversationUrl}`);
  if (id) keys.push(`${accountId}|id:${id}`, `${accountId}|${id}`);
  return keys;
};

const participantName = (item) => String(item?.participantName || item?.participant || item?.sender || "").trim();

const DialogRow = ({ item, onOpen, privateMode, t }) => {
  const name = participantName(item) || t("Собеседник");
  const image = getMessagePreviewImage(item);
  const [imageFailed, setImageFailed] = useState(false);
  const unread = Boolean(item?.unread);
  const outgoing = Boolean(item?.isOutgoing) || String(item?.lastMessageDirection || "").toLowerCase() === "outgoing";
  const text = String(item?.message || "").replace(/\s+/g, " ").trim();
  const adTitle = String(item?.adTitle || "").trim();
  const blur = privateMode ? "private-blur" : undefined;
  return (
    <button type="button" className={`dsb-dlg${unread ? " is-unread" : ""}`} onClick={() => onOpen && onOpen(item)}>
      <span className={`dsb-thumb${privateMode ? " private-blur-media" : ""}`}>
        {image && !imageFailed
          ? <img src={image} alt="" loading="lazy" onError={() => setImageFailed(true)} />
          : name.slice(0, 2).toUpperCase()}
      </span>
      <span className="dsb-dlg-text">
        <span className="dsb-dlg-top">
          <span className={`dsb-dlg-name ${blur || ""}`}>{name}</span>
          <span className={`dsb-dlg-ad ${blur || ""}`}>{adTitle}</span>
          <span className="dsb-dlg-time">{formatAgo(conversationMs(item), t)}</span>
        </span>
        <span className={`dsb-dlg-msg ${blur || ""}`} style={{ display: "block" }}>
          {outgoing ? `${t("Вы")}: ` : ""}{text || "—"}
        </span>
      </span>
      <span className={`dsb-unread-dot${unread ? "" : " is-off"}`} aria-label={unread ? t("Непрочитанное") : undefined} />
    </button>
  );
};

// ── Мини-график для показателя ─────────────────────────────────────────
const Sparkline = ({ values }) => {
  const width = 112;
  const height = 32;
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, values.length - 1);
  const points = values.map((value, index) => [index * step, height - 3 - (value / max) * (height - 8)]);
  const line = points.map((p, index) => `${index ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("");
  return (
    <svg className="dsb-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs>
        <linearGradient id="dsb-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--accent-400)" stopOpacity="0.28" />
          <stop offset="1" stopColor="var(--accent-400)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line}L${width} ${height}L0 ${height}Z`} fill="url(#dsb-spark-fill)" />
      <path d={line} fill="none" stroke="var(--accent-400)" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// Столбики приезжают от нуля: первый кадр рисуется пустым, на следующем —
// настоящая высота. Таймер — запасной путь для фоновой вкладки, где кадры
// не приходят.
const useGrown = () => {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setGrown(true));
    const timer = setTimeout(() => setGrown(true), 120);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, []);
  return grown;
};

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 7h10v10" /><path d="M7 17 17 7" />
  </svg>
);

export default function DashboardSummary({
  stats,
  accounts = [],
  accountMetrics = {},
  proxies = [],
  quickActions = [],
  describeAccount,
  conversations = [],
  ownerId = "",
  privateMode = false,
  onOpenConversation,
  onShowMessages,
  onShowAllAccounts,
  isPhoneView = false,
  t = (s) => s
}) {
  const grown = useGrown();

  // Диалоги без архивных — для списка, счётчиков и графика.
  const liveDialogs = useMemo(() => {
    const archived = readArchivedMap(ownerId);
    return (Array.isArray(conversations) ? conversations : [])
      .map((item, order) => ({ item, order, ms: conversationMs(item) }))
      .filter(({ item }) => !conversationKeys(item).some((key) => Number(archived[key] || 0) > 0));
  }, [conversations, ownerId]);

  // Последние: сверху непрочитанные, дальше по времени.
  const recentDialogs = useMemo(() => [...liveDialogs]
    .sort((left, right) => (Number(Boolean(right.item.unread)) - Number(Boolean(left.item.unread)))
      || (right.ms - left.ms)
      || (left.order - right.order))
    .slice(0, 5)
    .map(({ item }) => item), [liveDialogs]);

  // Активность за неделю: сколько диалогов ожило в каждый из последних 7 дней.
  const week = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 7 }, (_, index) => {
      const day = new Date(today);
      day.setDate(today.getDate() - (6 - index));
      return { start: day.getTime(), count: 0, label: day.toLocaleDateString(locale(), { weekday: "short" }).replace(/\.$/, "") };
    });
    const dayMs = 24 * 60 * 60 * 1000;
    liveDialogs.forEach(({ ms }) => {
      if (!ms) return;
      const index = Math.floor((ms - days[0].start) / dayMs);
      if (index >= 0 && index < 7) days[index].count += 1;
    });
    return days.map((day) => ({ ...day, label: day.label.charAt(0).toUpperCase() + day.label.slice(1) }));
  }, [liveDialogs]);

  if (!stats) return null;

  const accountList = Array.isArray(accounts) ? accounts : [];
  const offlineAccounts = accountList.filter((account) => account.status !== "active");
  const unreadDialogs = liveDialogs.filter(({ item }) => item.unread).length;
  const unreadByAccount = liveDialogs.reduce((map, { item }) => {
    if (item.unread) map[String(item.accountId)] = (map[String(item.accountId)] || 0) + 1;
    return map;
  }, {});
  const ownProxies = (Array.isArray(proxies) ? proxies : []).filter((proxy) => !proxy?.isBuiltin);
  const proxiesOk = ownProxies.filter((proxy) => proxy.status === "active").length;
  const proxiesFailed = ownProxies.filter((proxy) => proxy.status === "failed").length;
  const weekTotal = week.reduce((sum, day) => sum + day.count, 0);
  const weekMax = Math.max(1, ...week.map((day) => day.count));
  const dialogsWord = (n) => t(["{0} диалог", "{0} диалога", "{0} диалогов"][pluralIndex(n)]).replace("{0}", String(n));
  const nameOf = (account) => (describeAccount ? describeAccount(account) : { name: account.email || account.id, email: "" });

  const kpis = [
    {
      key: "accounts",
      label: t("Аккаунты онлайн"),
      value: stats.accounts.online,
      of: stats.accounts.total,
      note: offlineAccounts.length === 0
        ? { tone: "is-ok", text: stats.accounts.total ? t("все на связи") : t("аккаунтов нет") }
        : {
          tone: "is-warn",
          text: offlineAccounts.length === 1
            ? tf`1 офлайн — ${nameOf(offlineAccounts[0]).name}`
            : tf`${offlineAccounts.length} офлайн`,
          blur: offlineAccounts.length === 1
        }
    },
    {
      key: "unread",
      label: t("Непрочитанные"),
      value: stats.messages.unread,
      accent: stats.messages.unread > 0,
      note: { text: stats.messages.unread > 0 && unreadDialogs > 0
        ? t(pluralIndex(unreadDialogs) === 0 ? "в {0} диалоге" : "в {0} диалогах").replace("{0}", String(unreadDialogs))
        : t("всё прочитано") }
    },
    {
      key: "today",
      label: t("Сообщений сегодня"),
      value: stats.messages.today,
      note: { text: tf`всего ${Number(stats.messages.total || 0).toLocaleString(locale())}` },
      spark: week.map((day) => day.count)
    },
    {
      key: "proxies",
      label: t("Прокси в работе"),
      value: ownProxies.length ? proxiesOk : "—",
      of: ownProxies.length || null,
      note: !ownProxies.length
        ? { text: t("только встроенный") }
        : proxiesFailed > 0
          ? { tone: "is-bad", text: tf`${proxiesFailed} не отвечают` }
          : { tone: "is-ok", text: t("все отвечают") }
    }
  ];

  return (
    <div className="dsb">
      <section className="ui-card dsb-kpis">
        {kpis.map((kpi) => (
          <div key={kpi.key} className="dsb-kpi">
            <div className="dsb-kpi-label">{kpi.label}</div>
            <div className={`dsb-kpi-value${kpi.accent ? " is-accent" : ""}`}>
              {typeof kpi.value === "number" ? kpi.value.toLocaleString(locale()) : kpi.value}
              {kpi.of ? <small>/ {kpi.of}</small> : null}
            </div>
            <div className="dsb-kpi-note">
              {kpi.note.tone ? <span className={`dsb-dot ${kpi.note.tone}`} /> : null}
              <span className={kpi.note.blur && privateMode ? "private-blur" : undefined}>{kpi.note.text}</span>
            </div>
            {kpi.spark && !isPhoneView ? <Sparkline values={kpi.spark} /> : null}
          </div>
        ))}
      </section>

      <div className="dsb-row">
        <section className="ui-card dsb-list">
          <div className="ui-card-head">
            <h3 className="ui-card-title">{t("Последние диалоги")}</h3>
            <button type="button" className="ui-card-link" onClick={onShowMessages}>
              {t("Все диалоги")} <ChevronIcon />
            </button>
          </div>
          {recentDialogs.length === 0 && <div className="ui-card-empty">{t("Диалогов пока нет")}</div>}
          {recentDialogs.map((item, index) => (
            <DialogRow
              key={`dlg-${item.accountId}-${item.conversationId || item.conversationUrl || item.id || index}`}
              item={item}
              onOpen={onOpenConversation}
              privateMode={privateMode}
              t={t}
            />
          ))}
        </section>

        <div className="dsb-col">
          <section className="ui-card dsb-chart">
            <div className="ui-card-head">
              <h3 className="ui-card-title">{t("Активность за неделю")}</h3>
              <span className="dsb-chart-total">{dialogsWord(weekTotal)}</span>
            </div>
            <div className="dsb-bars">
              {week.map((day, index) => (
                <div key={day.start} className={`dsb-bar${index === week.length - 1 ? " is-today" : ""}`} title={dialogsWord(day.count)}>
                  <span style={{ height: grown ? `${Math.max(3, Math.round((day.count / weekMax) * 100))}%` : "3%" }} />
                  <label>{day.label}</label>
                </div>
              ))}
            </div>
          </section>

          <section className="ui-card dsb-actions">
            <div className="ui-card-head">
              <h3 className="ui-card-title">{t("Быстрые действия")}</h3>
            </div>
            <div className="dsb-actions-grid">
              {quickActions.map((action) => (
                <button key={action.id} type="button" className="dsb-action" onClick={action.onClick} disabled={action.disabled}>
                  {action.icon}
                  <span>{action.title}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="ui-card dsb-table">
        <div className="ui-card-head">
          <h3 className="ui-card-title">{t("Аккаунты")}</h3>
          <button type="button" className="ui-card-link" onClick={onShowAllAccounts}>
            {t("Все аккаунты")} <ChevronIcon />
          </button>
        </div>
        {accountList.length === 0 ? (
          <div className="ui-card-empty">{t("Аккаунты пока не добавлены")}</div>
        ) : (
          <>
            <div className="dsb-thead">
              <span>{t("Аккаунт")}</span>
              <span>{t("Статус")}</span>
              <span>{t("Прокси")}</span>
              <span>{t("Непрочитанные")}</span>
            </div>
            {accountList.slice(0, 6).map((account) => {
              const { name, email } = nameOf(account);
              const online = account.status === "active";
              const metricsUnread = Number(accountMetrics?.[account.id]?.unread);
              const unread = Number.isFinite(metricsUnread) && accountMetrics?.[account.id] ? metricsUnread : (unreadByAccount[String(account.id)] || 0);
              const proxyLabel = String(account.proxy || "").replace(/\s*\([^()]*\)\s*$/, "").trim();
              const blur = privateMode ? "private-blur" : undefined;
              return (
                <div key={`dsb-acc-${account.id}`} className="dsb-tr">
                  <span className="dsb-who">
                    <span className={`dsb-avatar${privateMode ? " private-blur-media" : ""}`}>{(name || "?").charAt(0).toUpperCase()}</span>
                    <span>
                      <b className={blur}>{name}</b>
                      {email ? <small className={blur}>{email}</small> : null}
                    </span>
                  </span>
                  <span className="dsb-status">
                    <span className={`dsb-dot ${online ? "is-ok" : "is-warn"}`} />
                    {online ? t("Онлайн") : t("Офлайн")}
                  </span>
                  <span className={`dsb-proxy ${blur || ""}`}>{proxyLabel || t("Без прокси")}</span>
                  <span className="dsb-num">
                    {unread > 0 ? <span className="dsb-pill">{unread}</span> : <span className="dsb-muted">—</span>}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </section>
    </div>
  );
}
